import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CautionMessageBox,
  Container,
  DisabledButton,
  Form,
  getTextInputProps,
  InputField,
  InputItem,
  Page,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  OrganizationType,
  useOrganization,
} from '@foxtrotplatform/platform-micro-site-core';
import { Select } from '@windmill/react-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from 'react-oidc-context';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createOrganization } from '../../../common/api/organization';
import { ApiError } from '../../../common/api/types';
import { DeveloperOrganizationContextProps } from '../../../common/providers/types';
import { addToast } from '../../../common/reducers/toasterSlice';
import countries from '../countries';
import { OrganizationCheck } from '../OrganizationCheck';
import { EmailInput } from './HelperComponents';
import {
  defaultOrganizationValues,
  OrganizationCreate,
  PERSONAL_EMAIL_DOMAINS,
} from './types';

const getSubtitle = (isPersonalEmail: boolean) => {
  if (!isPersonalEmail) {
    return (
      <div className="w-full rounded-md bg-green-50 h-20 flex flex-row p-4 gap-x-4 items-start">
        <FontAwesomeIcon
          className="h-4 w-4 mt-px text-green-500"
          icon={faCircleCheck}
        />
        <span className="text-green-700">
          You can now setup a development sandbox to develop your apps. Please
          create an organization to continue.
        </span>
      </div>
    );
  }

  return (
    <div className="w-full rounded-md bg-yellow-50 h-20 flex flex-row p-4 gap-x-4 items-start">
      <FontAwesomeIcon
        className="h-4 w-4 mt-px text-yellow-300"
        icon={faCircleCheck}
      />
      <span className="text-yellow-700">
        Access to development sandboxes created with email addresses that are
        not business verified might be limited and the sandbox might be
        disabled.
      </span>
    </div>
  );
};

export const CreateOrganization: React.FunctionComponent =
  (): JSX.Element | null => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
      refreshOrganizations,
      isOrganizationsReady,
      requiresOrgOnboarding,
    } = useOrganization() as DeveloperOrganizationContextProps;
    const { user } = useAuth();
    const [acknowledged, setAcknowledged] = useState(false);
    const [showCompanyDomainAlert, setShowCompanyDomainAlert] = useState(false);
    const [isUserEmailBusinessDomain, setIsUserEmailBusinessDomain] =
      useState(false);

    const acknowledgedButtonClick = useCallback(() => {
      setAcknowledged(true);
    }, []);

    const {
      handleSubmit,
      register,
      reset,
      watch,
      setValue,
      formState: { errors, isValid },
    } = useForm<OrganizationCreate>({
      mode: 'onBlur',
      defaultValues: defaultOrganizationValues,
    });
    const type = watch('type');
    const email = watch('email');

    useEffect(() => {
      if (user && user.profile && user.profile.email && !email) {
        setValue('email', user?.profile.email);
        setValue('type', OrganizationType.business);

        const foundPersonalEmailDomain = PERSONAL_EMAIL_DOMAINS.find(
          (emailDomain) =>
            user.profile.email?.toLowerCase().endsWith(`@${emailDomain}`)
        );

        setIsUserEmailBusinessDomain(!foundPersonalEmailDomain);
      }
    }, [email, user?.profile.email, setValue]);

    useEffect(() => {
      if (email) {
        const foundPersonalEmailDomain = PERSONAL_EMAIL_DOMAINS.find(
          (emailDomain) => email.toLowerCase().endsWith(`@${emailDomain}`)
        );

        setShowCompanyDomainAlert(foundPersonalEmailDomain !== undefined);
      }
    }, [email]);

    const onSubmit = handleSubmit(
      async ({
        type,
        firstName,
        lastName,
        companyName,
        ...restOrganization
      }: OrganizationCreate) => {
        const name =
          type === OrganizationType.business
            ? companyName
            : `${firstName} ${lastName}`;
        const organization =
          type === OrganizationType.business
            ? { type, companyName, firstName, lastName, ...restOrganization }
            : { type, firstName, lastName, ...restOrganization };

        try {
          const { id, requiresEmailValidation } = await createOrganization(
            organization
          );

          dispatch(
            addToast({
              message: `Successfully created organization: ${name}`,
            })
          );
          reset();

          const nextPage = requiresEmailValidation ? 'verify' : 'success';
          refreshOrganizations();
          navigate(`/organization/${id}/${nextPage}`);
        } catch (e) {
          let message = `failed to create organization: ${name}`;
          if (e instanceof ApiError) {
            message = e.response.error.description;
          }

          dispatch(
            addToast({
              message,
              isError: true,
            })
          );
          reset();
        }
      }
    );

    const subtitle = getSubtitle(showCompanyDomainAlert);

    useEffect(() => {
      if (isOrganizationsReady && !requiresOrgOnboarding) {
        navigate('/organization');
      }
    }, [isOrganizationsReady, requiresOrgOnboarding]);

    return (
      <Page className="flex-col items-center">
        <Container size="medium">
          <OrganizationCheck />
          {!acknowledged && (
            <CautionMessageBox
              actionButtonProps={{
                disabled: acknowledged,
              }}
              actionTitle="Agree & Continue"
              className="mb-os1-spacing"
              description={
                <div className="mb-3">
                  <span className="text-gray-500">
                    We did not find an organization that matches the domain
                    associated with your email address
                  </span>
                  <div className="ml-4">
                    <li className="text-gray-500">
                      Organizations represent your company on the Platform
                    </li>
                    <li className="text-gray-500">
                      Only one organization can be associated with a domain
                    </li>
                    <li className="text-gray-500">
                      If you lead and manage teams then you can register your
                      company with us and create a development sandbox for your
                      company and team
                    </li>
                    <li className="text-gray-500">
                      After doing so, you and other developers in your
                      organization develop apps using our APIs installed in the
                      development sandbox tenant
                    </li>
                  </div>
                </div>
              }
              descriptionClassName="mt-5 mb-2"
              onActionClick={acknowledgedButtonClick}
              title="Cannot find organization that matched your domain"
              titleClassName=""
            />
          )}
          <div
            className={`${
              !acknowledged ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Form
              className="text-sm"
              onSubmit={onSubmit}
              subtitle={subtitle}
              title="Create an Organization"
            >
              <InputItem title="What is your type of organization">
                <div className="flex flex-row gap-x-4">
                  <div
                    className={`${
                      type === OrganizationType.individual
                        ? 'bg-indigo-100'
                        : ''
                    } py-2 px-3 rounded-md`}
                  >
                    <label htmlFor="field-individual">
                      <input
                        disabled={!acknowledged}
                        type="radio"
                        {...getTextInputProps({
                          inputKey: 'type',
                          errors,
                          register,
                        })}
                        className={`bg-brand-blue h-4 w-4 shadow focus:ring-transparent mr-2 ${
                          !acknowledged ? 'cursor-not-allowed' : ''
                        }`}
                        id="field-individual"
                        name="type"
                        value="individual"
                      />
                      Individual
                    </label>
                  </div>
                  <div
                    className={`${
                      type === OrganizationType.business ? 'bg-indigo-100' : ''
                    } py-2 px-3 rounded-md`}
                  >
                    <label htmlFor="field-business">
                      <input
                        disabled={!acknowledged}
                        type="radio"
                        {...getTextInputProps({
                          inputKey: 'type',
                          errors,
                          register,
                        })}
                        className={`bg-brand-blue h-4 w-4 shadow focus:ring-transparent mr-2 ${
                          !acknowledged ? 'cursor-not-allowed' : ''
                        }`}
                        id="field-business"
                        name="type"
                        value="business"
                      />
                      Business
                    </label>
                  </div>
                </div>
              </InputItem>
              <div className="flex flex-row gap-x-3">
                <EmailInput
                  disabled={!acknowledged}
                  email={email}
                  errors={errors}
                  isUserEmailBusinessDomain={isUserEmailBusinessDomain}
                  register={register}
                  showCompanyDomainAlert={showCompanyDomainAlert}
                />
                {type === OrganizationType.business && (
                  <InputField
                    className={`${!acknowledged ? 'cursor-not-allowed' : ''}`}
                    disabled={!acknowledged}
                    errors={errors}
                    inputKey="companyName"
                    register={register}
                    registerProps={{
                      required: type === OrganizationType.business,
                      pattern: {
                        value: /^null|$/i,
                        message: 'Please add company name',
                      },
                    }}
                    title="Company Name"
                  />
                )}
              </div>
              <div className="flex flex-row gap-x-3">
                <InputField
                  className={`${!acknowledged ? 'cursor-not-allowed' : ''}`}
                  disabled={!acknowledged}
                  errors={errors}
                  inputKey="firstName"
                  register={register}
                  registerProps={{
                    required: true,
                    pattern: {
                      value: /^null|$/i,
                      message: 'Please add first name',
                    },
                  }}
                  title="First Name"
                />
                <InputField
                  className={`${!acknowledged ? 'cursor-not-allowed' : ''}`}
                  disabled={!acknowledged}
                  errors={errors}
                  inputKey="lastName"
                  register={register}
                  registerProps={{
                    required: true,
                    pattern: {
                      value: /^null|$/i,
                      message: 'Please add last name',
                    },
                  }}
                  title="Last Name"
                />
              </div>
              <div className="flex flex-row gap-x-3">
                <InputField
                  className={`${!acknowledged ? 'cursor-not-allowed' : ''}`}
                  disabled={!acknowledged}
                  errors={errors}
                  inputKey="website"
                  register={register}
                  registerProps={{
                    required: false,
                    pattern: {
                      value:
                        /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/i,
                      message: 'Invalid website',
                    },
                  }}
                  title="Website"
                />
                <InputField
                  className={`${!acknowledged ? 'cursor-not-allowed' : ''}`}
                  disabled={!acknowledged}
                  errors={errors}
                  inputKey="domain"
                  register={register}
                  registerProps={{
                    required: false,
                    pattern: {
                      value:
                        /^(?!www.)[a-z0-9]+([-]{1}[a-z0-9]+)*\.[a-z]{2,6}$/i,
                      message: 'Invalid domain',
                    },
                  }}
                  title="Domain"
                />
              </div>

              <InputItem title="Country">
                <Select
                  css=""
                  disabled={!acknowledged}
                  {...getTextInputProps({
                    inputKey: 'country',
                    errors,
                    register,
                    className: `${
                      !acknowledged ? 'cursor-not-allowed' : ''
                    } shadow-md`,
                  })}
                >
                  <option value="" />
                  {countries.map(({ code, name }) => {
                    return (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    );
                  })}
                </Select>
              </InputItem>
              <div className="flex flex-col justify-between">
                <div className="flex flex-row items-center text-black font-light text-sm pb-3">
                  <input
                    disabled={!acknowledged}
                    type="checkbox"
                    {...getTextInputProps({
                      inputKey: 'marketingPromotions',
                      errors,
                      register,
                      registerProps: { required: false },
                    })}
                    className={`bg-brand-blue rounded h-4 w-4 shadow mr-2 ${
                      !acknowledged ? 'cursor-not-allowed' : ''
                    }`}
                  />
                  I agree to receive marketing communications from OS1 Developer
                  Platform
                </div>
                <DisabledButton
                  className="h-10 mt-3 rounded-md w-48"
                  isActive={acknowledged && isValid}
                >
                  Create Organization
                </DisabledButton>
              </div>
            </Form>
          </div>
        </Container>
      </Page>
    );
  };
